import { useEffect } from 'react';
import { useNavigate, createFileRoute } from '@tanstack/react-router';
import { useSelfServeAccess } from '@/hooks/useSelfServeAccess';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useOrganization, useSyncTenantFromParams } from '@/context/organization-context';
import { paths } from '@/routes/paths';
import { ManagedSectionView } from '@/sections/dashboard/sections/managed-section-view';
import { useManagedBrandStatus } from '@/sections/dashboard/sections/use-managed-brand';
import { useEmailDocumentSectionsControllerGetOne } from 'src/apis/backend/generated/email-document-sections/email-document-sections';
import {
  EmailDocumentSectionEditor,
  type EmailDocumentSectionEditorVariant,
} from '@/sections/admin/email-document-sections/email-document-section-editor';
import { RawHtmlSectionChatEditor } from '@/sections/dashboard/sections/raw-html-section-chat-editor';
import { NewSectionModePicker } from '@/sections/dashboard/sections/new-section-mode-picker';

export const Route = createFileRoute('/_authed/dashboard_/o/$orgId/c/$clientId/sections/$id')({
  component: DashboardSectionEditorPage,
});

function DashboardSectionEditorPage() {
  const { id, orgId: routeOrgId, clientId: routeClientId } = Route.useParams();
  useSyncTenantFromParams(routeOrgId, routeClientId);
  const { activeOrganization, activeClient } = useOrganization();

  if (!activeOrganization || !activeClient) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Select an organization and client to edit sections.</Alert>
      </Box>
    );
  }

  const variant: EmailDocumentSectionEditorVariant = {
    kind: 'tenant',
    orgId: activeOrganization.id,
    clientId: activeClient.id,
    targetScope: 'client',
    backPath: paths.dashboard.sections,
    editPath: paths.dashboard.sectionEdit,
  };

  if (id === 'new') {
    return (
      <NewSectionPage orgId={activeOrganization.id} clientId={activeClient.id} variant={variant} />
    );
  }

  return (
    <SectionEditorRouter
      sectionId={id}
      orgId={activeOrganization.id}
      clientId={activeClient.id}
      variant={variant}
    />
  );
}

/**
 * The new-section picker, for the brands that build their own.
 *
 * Every way the picker offers is the builder, which is what Base buys. Any other
 * brand in the library has its sections made by us, so it is sent back to the
 * library, which carries the request for one. The library no longer links here
 * for those brands; this covers a typed or bookmarked URL. Waits on the tier so
 * a Base brand is not bounced off its own picker on first paint.
 */
function NewSectionPage({
  orgId,
  clientId,
  variant,
}: {
  orgId: string;
  clientId: string;
  variant: EmailDocumentSectionEditorVariant;
}) {
  const navigate = useNavigate();
  const { enabled: selfServe, isLoading } = useSelfServeAccess();
  const library = paths.dashboard.tenant(orgId, clientId).sections;

  useEffect(() => {
    if (isLoading || selfServe) return;
    navigate({ to: library, replace: true });
  }, [isLoading, selfServe, navigate, library]);

  if (isLoading || !selfServe) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <NewSectionModePicker
      orgId={orgId}
      clientId={clientId}
      backPath={paths.dashboard.sections}
      editPath={paths.dashboard.sectionEdit}
      renderBuildMode={() => <EmailDocumentSectionEditor variant={variant} />}
    />
  );
}

// Picks the surface a section opens on. A brand with an active White Glove
// engagement does not edit its sections: nexmail makes them, so any row, in any
// storage format, opens read-only with the way to ask the team for a change.
// Every other brand branches on the row's storage format: 'html' rows get the
// chat editor; 'node' rows fall through to the existing structured EmailBuilder
// editor. We do the fetch here (rather than in each editor) so the route stays
// the single source of truth for "which surface" and the editors don't need to
// re-derive it. Both the row and the engagement are waited on, so neither kind
// of brand is shown the other's surface first.
function SectionEditorRouter({
  sectionId,
  orgId,
  clientId,
  variant,
}: {
  sectionId: string;
  orgId: string;
  clientId: string;
  variant: EmailDocumentSectionEditorVariant;
}) {
  const { data, isLoading, error } = useEmailDocumentSectionsControllerGetOne(
    orgId,
    clientId,
    sectionId,
  );
  const { managed, isLoading: managedLoading } = useManagedBrandStatus(orgId, clientId);

  if (isLoading || managedLoading) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load section: {(error as Error).message}
        </Alert>
      </Box>
    );
  }

  if (managed) {
    return data ? (
      <ManagedSectionView
        orgId={orgId}
        clientId={clientId}
        section={data}
        backPath={paths.dashboard.sections}
      />
    ) : null;
  }

  if (data?.sourceFormat === 'html') {
    return (
      <RawHtmlSectionChatEditor
        sectionId={sectionId}
        orgId={orgId}
        clientId={clientId}
        backPath={paths.dashboard.sections}
      />
    );
  }

  // Default: structured node editor (existing behavior).
  return <EmailDocumentSectionEditor sectionId={sectionId} variant={variant} />;
}
